/**
 * Seller Rating - Shows star rating for marketplace sellers
 */

import { Star } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface Props {
  rating: number | null;
  count?: number;
  size?: 'sm' | 'md';
  showCount?: boolean;
}

export default function SellerRating({ rating, count = 0, size = 'sm', showCount = true }: Props) {
  const { t } = useLanguage();

  if (rating === null) {
    return (
      <span className={`text-gray-400 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
        {t('marketplace.seller.noRatings', 'No ratings yet')}
      </span>
    );
  }

  const starSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="inline-flex items-center gap-1">
      <div className="flex items-center">
        {/* Full stars */}
        {Array.from({ length: fullStars }).map((_, i) => (
          <Star key={`full-${i}`} className={`${starSize} fill-star-rating text-star-rating`} />
        ))}
        {/* Half star */}
        {hasHalfStar && (
          <div className="relative">
            <Star className={`${starSize} text-gray-300`} />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <Star className={`${starSize} fill-star-rating text-star-rating`} />
            </div>
          </div>
        )}
        {/* Empty stars */}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <Star key={`empty-${i}`} className={`${starSize} text-gray-300`} />
        ))}
      </div>
      {showCount && (
        <span className={`text-gray-500 ${textSize}`}>
          {rating.toFixed(1)} ({count})
        </span>
      )}
    </div>
  );
}
